import { Video, Calendar, MessageCircle, Globe, Link2, CalendarClock, Mail, Phone, Linkedin, Instagram, Facebook, Twitter, Brain, Sparkles, Briefcase, Compass, Languages, UserPlus } from "lucide-react";

export const CONNECTION_TYPES = [
  { value: "email", label: "Email", icon: Mail },
  { value: "phone", label: "Teléfono", icon: Phone },
  { value: "website", label: "Sitio web", icon: Globe },
  { value: "booking", label: "Reservas", icon: CalendarClock },
  { value: "video", label: "Videollamada", icon: Video },
];

export function connectionMeta(type) {
  return CONNECTION_TYPES.find((c) => c.value === type) || { value: type, label: "Enlace", icon: Link2 };
}

export const PROFESSIONAL_LINK_TYPES = [
  { value: "website", label: "Website", icon: Globe },
  { value: "linkedin", label: "LinkedIn", icon: Linkedin },
  { value: "instagram", label: "Instagram", icon: Instagram },
  { value: "facebook", label: "Facebook", icon: Facebook },
  { value: "x", label: "X / Twitter", icon: Twitter },
  { value: "other", label: "Other", icon: Link2 },
];

export function linkMeta(type) {
  return PROFESSIONAL_LINK_TYPES.find((c) => c.value === type) || PROFESSIONAL_LINK_TYPES[PROFESSIONAL_LINK_TYPES.length - 1];
}

// Normalize a stored web URL into a safe external href. Prepends https:// when a
// protocol is missing so browsers never treat it as an internal SPA route.
// mailto:/tel: (and other non-web schemes) are preserved untouched.
export function toExternalUrl(raw) {
  const url = (raw || "").toString().trim();
  if (!url) return url;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url; // already has a scheme (http, https, mailto, tel, ...)
  if (url.startsWith("//")) return `https:${url}`;
  return `https://${url}`;
}

export const LANGUAGES = [
  "Español", "English", "Português", "Français", "Italiano", "Deutsch",
  "Català", "Galego", "Euskara", "Guaraní", "Quechua",
];

// Display labels per interface language (stored values stay the stable strings above).
// ISO codes (es/en/pt/…) are also accepted as canonical values for graceful rendering.
const LANGUAGE_LABELS = {
  "Español": ["Español", "Spanish"], "English": ["Inglés", "English"],
  "Português": ["Portugués", "Portuguese"], "Français": ["Francés", "French"],
  "Italiano": ["Italiano", "Italian"], "Deutsch": ["Alemán", "German"],
  "Català": ["Catalán", "Catalan"], "Galego": ["Gallego", "Galician"],
  "Euskara": ["Euskera", "Basque"], "Guaraní": ["Guaraní", "Guarani"],
  "Quechua": ["Quechua", "Quechua"],
  es: ["Español", "Spanish"], en: ["Inglés", "English"], pt: ["Portugués", "Portuguese"],
  fr: ["Francés", "French"], it: ["Italiano", "Italian"], de: ["Alemán", "German"],
  ca: ["Catalán", "Catalan"], gl: ["Gallego", "Galician"], eu: ["Euskera", "Basque"],
  gn: ["Guaraní", "Guarani"], qu: ["Quechua", "Quechua"],
};
export function languageLabel(value, lang) {
  const m = LANGUAGE_LABELS[value];
  // Labels are [es, en]; am has no dedicated label → English (es only for Spanish UI).
  return m ? (lang === "es" ? m[0] : m[1]) : value;
}

export const COUNTRIES = [
  "España", "México", "Argentina", "Colombia", "Chile", "Perú", "Venezuela",
  "Ecuador", "Guatemala", "Cuba", "Bolivia", "República Dominicana", "Honduras",
  "Paraguay", "El Salvador", "Nicaragua", "Costa Rica", "Panamá", "Uruguay",
  "Puerto Rico", "Estados Unidos", "Reino Unido", "Canadá", "Australia",
];

// Broader standardized list used for the "Otro país" searchable selector.
export const WORLD_COUNTRIES = [
  "Afganistán", "Alemania", "Arabia Saudita", "Argelia", "Argentina", "Australia", "Austria",
  "Bélgica", "Bolivia", "Brasil", "Bulgaria", "Canadá", "Chile", "China", "Colombia",
  "Corea del Sur", "Costa Rica", "Croacia", "Cuba", "Dinamarca", "Ecuador", "Egipto",
  "El Salvador", "Emiratos Árabes Unidos", "Eslovaquia", "Eslovenia", "España", "Estados Unidos",
  "Estonia", "Filipinas", "Finlandia", "Francia", "Grecia", "Guatemala", "Honduras", "Hungría",
  "India", "Indonesia", "Irlanda", "Islandia", "Israel", "Italia", "Japón", "Kenia", "Líbano",
  "Luxemburgo", "Malasia", "Marruecos", "México", "Nicaragua", "Nigeria", "Noruega",
  "Nueva Zelanda", "Países Bajos", "Panamá", "Paraguay", "Perú", "Polonia", "Portugal",
  "Puerto Rico", "Reino Unido", "República Checa", "República Dominicana", "Rumanía", "Rusia",
  "Singapur", "Sudáfrica", "Suecia", "Suiza", "Tailandia", "Turquía", "Ucrania", "Uruguay",
  "Venezuela", "Vietnam",
];

// English display labels for country values (internal values stay in Spanish).
export const COUNTRY_LABELS_EN = {
  "Afganistán": "Afghanistan", "Alemania": "Germany", "Arabia Saudita": "Saudi Arabia",
  "Argelia": "Algeria", "Argentina": "Argentina", "Australia": "Australia", "Austria": "Austria",
  "Bélgica": "Belgium", "Bolivia": "Bolivia", "Brasil": "Brazil", "Bulgaria": "Bulgaria",
  "Canadá": "Canada", "Chile": "Chile", "China": "China", "Colombia": "Colombia",
  "Corea del Sur": "South Korea", "Costa Rica": "Costa Rica", "Croacia": "Croatia", "Cuba": "Cuba",
  "Dinamarca": "Denmark", "Ecuador": "Ecuador", "Egipto": "Egypt", "El Salvador": "El Salvador",
  "Emiratos Árabes Unidos": "United Arab Emirates", "Eslovaquia": "Slovakia", "Eslovenia": "Slovenia",
  "España": "Spain", "Estados Unidos": "United States", "Estonia": "Estonia", "Filipinas": "Philippines",
  "Finlandia": "Finland", "Francia": "France", "Grecia": "Greece", "Guatemala": "Guatemala",
  "Honduras": "Honduras", "Hungría": "Hungary", "India": "India", "Indonesia": "Indonesia",
  "Irlanda": "Ireland", "Islandia": "Iceland", "Israel": "Israel", "Italia": "Italy", "Japón": "Japan",
  "Kenia": "Kenya", "Líbano": "Lebanon", "Luxemburgo": "Luxembourg", "Malasia": "Malaysia",
  "Marruecos": "Morocco", "México": "Mexico", "Nicaragua": "Nicaragua", "Nigeria": "Nigeria",
  "Noruega": "Norway", "Nueva Zelanda": "New Zealand", "Países Bajos": "Netherlands", "Panamá": "Panama",
  "Paraguay": "Paraguay", "Perú": "Peru", "Polonia": "Poland", "Portugal": "Portugal",
  "Puerto Rico": "Puerto Rico", "Reino Unido": "United Kingdom", "República Checa": "Czech Republic",
  "República Dominicana": "Dominican Republic", "Rumanía": "Romania", "Rusia": "Russia",
  "Singapur": "Singapore", "Sudáfrica": "South Africa", "Suecia": "Sweden", "Suiza": "Switzerland",
  "Tailandia": "Thailand", "Turquía": "Turkey", "Ucrania": "Ukraine", "Uruguay": "Uruguay",
  "Venezuela": "Venezuela", "Vietnam": "Vietnam",
};
export function countryLabel(value, lang) {
  // Stored value is Spanish; am has no Amharic country labels → English (es only for Spanish UI).
  return lang === "es" ? value : (COUNTRY_LABELS_EN[value] || value);
}

export const SERVICE_FORMATS = ["online", "in_person", "both"];export const SERVICE_TYPES = ["paid", "volunteer", "both"];

// Taxonomy: Category (group) → Profession → Specialties/capabilities.
// `group` = public taxonomy category. `category` = INTERNAL clinical vs non_clinical (safety/validation only).
export const PROFESSION_CATEGORIES = [
  { value: "creative_media", es: "Creativo y medios", en: "Creative & media", am: "ፈጠራ እና መገናኛ ብዙኃን" },
  { value: "beauty_fashion", es: "Belleza, moda y estilo", en: "Beauty, fashion & style", am: "ውበት፣ ፋሽን እና ስታይል" },
  { value: "tech_product", es: "Tecnología y producto", en: "Technology & product", am: "ቴክኖሎጂ እና ምርት" },
  { value: "production_ops", es: "Producción y operaciones", en: "Production & operations", am: "ምርት እና ክንውኖች" },
  { value: "manufacturing", es: "Manufactura", en: "Manufacturing", am: "ማምረቻ" },
  { value: "logistics", es: "Logística y cadena de suministro", en: "Logistics & supply chain", am: "ሎጂስቲክስ እና የአቅርቦት ሰንሰለት" },
  { value: "business_growth", es: "Negocios y crecimiento", en: "Business & growth", am: "ንግድ እና እድገት" },
  { value: "professional_services", es: "Servicios profesionales", en: "Professional services", am: "የሙያ አገልግሎቶች" },
  { value: "other", es: "Otro", en: "Other", am: "ሌላ" },
];

const P = (value, es, en, group, category = "non_clinical") => ({ value, es, en, group, category });

// Canonical profession values are STABLE regardless of language. Existing values preserved.
export const PROFESSIONS = [
  // Creativo y medios
  P("director", "Director/a", "Director", "creative_media"),
  P("producer", "Productor/a", "Producer", "creative_media"),
  P("photographer", "Fotógrafo/a", "Photographer", "creative_media"),
  P("videographer", "Videógrafo/a", "Videographer", "creative_media"),
  P("video_editor", "Editor/a de video", "Video Editor", "creative_media"),
  P("graphic_designer", "Diseñador/a gráfico/a", "Graphic Designer", "creative_media"),
  P("creative_director", "Director/a creativo/a", "Creative Director", "creative_media"),
  P("musician", "Músico/a", "Musician", "creative_media"),
  P("sound_engineer", "Ingeniero/a de sonido", "Sound Engineer", "creative_media"),
  P("other_creative_media", "Otro (creativo y medios)", "Other (creative & media)", "creative_media"),
  // Belleza, moda y estilo
  P("barber", "Barbero/a", "Barber", "beauty_fashion"),
  P("hairstylist", "Peluquero/a", "Hairstylist", "beauty_fashion"),
  P("stylist", "Estilista", "Stylist", "beauty_fashion"),
  P("makeup_artist", "Maquillador/a", "Makeup Artist", "beauty_fashion"),
  P("fashion_designer", "Diseñador/a de moda", "Fashion Designer", "beauty_fashion"),
  P("wardrobe_stylist", "Estilista de vestuario", "Wardrobe Stylist", "beauty_fashion"),
  P("other_beauty_fashion", "Otro (belleza, moda y estilo)", "Other (beauty, fashion & style)", "beauty_fashion"),
  // Tecnología y producto
  P("ux_ui_designer", "Diseñador/a UX/UI", "UX/UI Designer", "tech_product"),
  P("developer", "Desarrollador/a", "Developer", "tech_product"),
  P("software_engineer", "Ingeniero/a de software", "Software Engineer", "tech_product"),
  P("product_manager", "Product Manager", "Product Manager", "tech_product"),
  P("data_specialist", "Especialista en datos", "Data Specialist", "tech_product"),
  P("ai_automation_specialist", "Especialista en automatización / IA", "Automation / AI Specialist", "tech_product"),
  P("other_tech_product", "Otro (tecnología y producto)", "Other (technology & product)", "tech_product"),
  // Producción y operaciones
  P("production_manager", "Gerente de producción", "Production Manager", "production_ops"),
  P("production_coordinator", "Coordinador/a de producción", "Production Coordinator", "production_ops"),
  P("operations_specialist", "Especialista en operaciones", "Operations Specialist", "production_ops"),
  P("purchasing_specialist", "Especialista en compras / sourcing", "Purchasing / Sourcing Specialist", "production_ops"),
  P("qc_specialist", "Especialista en control de calidad", "Quality Control Specialist", "production_ops"),
  P("other_production_ops", "Otro (producción y operaciones)", "Other (production & operations)", "production_ops"),
  // Manufactura
  P("manufacturing_specialist", "Especialista en manufactura", "Manufacturing Specialist", "manufacturing"),
  P("process_engineer", "Ingeniero/a de procesos", "Process Engineer", "manufacturing"),
  P("product_development_specialist", "Especialista en desarrollo de producto", "Product Development Specialist", "manufacturing"),
  P("factory_sourcing_specialist", "Especialista en sourcing de fábricas", "Factory Sourcing Specialist", "manufacturing"),
  P("mfg_qc_specialist", "Especialista en control de calidad (manufactura)", "Quality Control Specialist (manufacturing)", "manufacturing"),
  P("packaging_specialist", "Especialista en empaque / packaging", "Packaging Specialist", "manufacturing"),
  P("other_manufacturing", "Otro (manufactura)", "Other (manufacturing)", "manufacturing"),
  // Logística y cadena de suministro
  P("logistics_specialist", "Especialista en logística", "Logistics Specialist", "logistics"),
  P("supply_chain_specialist", "Especialista en cadena de suministro", "Supply Chain Specialist", "logistics"),
  P("import_export_specialist", "Especialista en importación / exportación", "Import / Export Specialist", "logistics"),
  P("transport_specialist", "Especialista en transporte", "Transport Specialist", "logistics"),
  P("distribution_specialist", "Especialista en distribución", "Distribution Specialist", "logistics"),
  P("freight_specialist", "Especialista en carga / freight", "Freight Specialist", "logistics"),
  P("customs_specialist", "Especialista en aduanas", "Customs Specialist", "logistics"),
  P("other_logistics", "Otro (logística)", "Other (logistics)", "logistics"),
  // Negocios y crecimiento
  P("business_coach", "Mentor/a de negocios / Emprendedor/a", "Business Mentor / Entrepreneur", "business_growth"),
  P("business_consultant", "Consultor/a de negocios", "Business Consultant", "business_growth"),
  P("strategy_specialist", "Especialista en estrategia", "Strategy Specialist", "business_growth"),
  P("marketing_specialist", "Especialista en marketing", "Marketing Specialist", "business_growth"),
  P("sales_specialist", "Especialista en ventas", "Sales Specialist", "business_growth"),
  P("business_development_specialist", "Especialista en desarrollo de negocios", "Business Development Specialist", "business_growth"),
  P("finance_specialist", "Especialista en finanzas", "Finance Specialist", "business_growth"),
  P("international_trade_specialist", "Especialista en comercio internacional", "International Trade Specialist", "business_growth"),
  P("other_business_growth", "Otro (negocios y crecimiento)", "Other (business & growth)", "business_growth"),
  // Servicios profesionales (incl. legacy clinical + non-clinical)
  P("psychologist", "Psicólogo/a", "Psychologist", "professional_services", "clinical"),
  P("coach", "Coach", "Coach", "professional_services"),
  P("career_coach", "Orientador/a profesional / de carrera", "Professional / Career Guide", "professional_services"),
  P("communication_coach", "Especialista en comunicación", "Communication Specialist", "professional_services"),
  P("language_teacher", "Profesor/a de idiomas", "Language Teacher", "professional_services"),
  P("consultant", "Consultor/a", "Consultant", "professional_services"),
  P("counselor", "Consejero/a", "Counselor", "professional_services", "clinical"),
  P("social_worker", "Trabajador/a social", "Social Worker", "professional_services", "clinical"),
  P("psychiatrist", "Psiquiatra", "Psychiatrist", "professional_services", "clinical"),
  P("other_clinical", "Otro profesional clínico", "Other clinical professional", "professional_services", "clinical"),
  P("other_non_clinical", "Otro profesional", "Other professional", "professional_services"),
  // Otro (catch-all category)
  P("other_general", "Otro", "Other", "other"),
];

export function professionMeta(value) {
  return PROFESSIONS.find((p) => p.value === value);
}

// Professions belonging to a taxonomy category (group), in declared order.
export function professionsByCategory(group) {
  return PROFESSIONS.filter((p) => p.group === group);
}

export function categoryOfProfession(profession) {
  return PROFESSIONS.find((p) => p.value === profession)?.group || null;
}

export function professionCategoryLabel(group, lang) {
  const c = PROFESSION_CATEGORIES.find((x) => x.value === group);
  if (!c) return "";
  if (lang === "es") return c.es;
  if (lang === "am") return c.am ?? c.en ?? c.es;
  return c.en ?? c.es;
}

export function isOtherProfession(value) {
  return typeof value === "string" && value.startsWith("other_");
}

// Global canonical specialty key → localized label. Keys are language-neutral and stable.
export const SPECIALTY_LABELS = {
  film: { es: "Cine", en: "Film" }, documentaries: { es: "Documentales", en: "Documentaries" },
  commercials: { es: "Comerciales", en: "Commercials" }, music_videos: { es: "Videoclips", en: "Music videos" },
  digital_content: { es: "Contenido digital", en: "Digital content" }, directing_actors: { es: "Dirección de actores", en: "Directing actors" },
  production: { es: "Producción", en: "Production" }, advertising: { es: "Publicidad", en: "Advertising" },
  events: { es: "Eventos", en: "Events" }, budgeting: { es: "Presupuestos", en: "Budgeting" }, casting: { es: "Casting", en: "Casting" },
  portrait: { es: "Retrato", en: "Portrait" }, product: { es: "Producto", en: "Product" }, fashion: { es: "Moda", en: "Fashion" },
  editorial: { es: "Editorial", en: "Editorial" }, commercial: { es: "Comercial", en: "Commercial" },
  ux_research: { es: "UX Research", en: "UX Research" }, ux_design: { es: "UX Design", en: "UX Design" },
  ui_design: { es: "UI Design", en: "UI Design" }, prototyping: { es: "Prototipos", en: "Prototyping" },
  design_systems: { es: "Design Systems", en: "Design Systems" }, usability_testing: { es: "Pruebas de usabilidad", en: "Usability testing" },
  product_strategy: { es: "Estrategia de producto", en: "Product strategy" },
  frontend: { es: "Frontend", en: "Frontend" }, backend: { es: "Backend", en: "Backend" }, fullstack: { es: "Full-stack", en: "Full-stack" },
  mobile: { es: "Móvil", en: "Mobile" }, apis: { es: "APIs", en: "APIs" }, databases: { es: "Bases de datos", en: "Databases" }, devops: { es: "DevOps", en: "DevOps" },
  cuts: { es: "Cortes", en: "Cuts" }, fades: { es: "Fades", en: "Fades" }, grooming: { es: "Grooming", en: "Grooming" },
  beard: { es: "Barba", en: "Beard" }, editorial_production: { es: "Producción audiovisual/editorial", en: "Editorial/audiovisual production" },
  color: { es: "Color", en: "Color" }, styling: { es: "Peinados", en: "Styling" }, bridal: { es: "Novias", en: "Bridal" },
  customs: { es: "Aduanas", en: "Customs" }, documentation: { es: "Documentación", en: "Documentation" }, sourcing: { es: "Sourcing", en: "Sourcing" },
  freight: { es: "Carga / freight", en: "Freight" }, distribution: { es: "Distribución", en: "Distribution" },
  usa: { es: "EE.UU.", en: "USA" }, latin_america: { es: "Latinoamérica", en: "Latin America" },
  last_mile: { es: "Última milla", en: "Last mile" }, warehousing: { es: "Almacenamiento", en: "Warehousing" },
  inventory: { es: "Inventario", en: "Inventory" }, route_optimization: { es: "Optimización de rutas", en: "Route optimization" },
  factory_sourcing: { es: "Sourcing de fábricas", en: "Factory sourcing" }, quality_control: { es: "Control de calidad", en: "Quality control" },
  product_development: { es: "Desarrollo de producto", en: "Product development" }, packaging: { es: "Packaging", en: "Packaging" },
  production_scaling: { es: "Escalado de producción", en: "Production scaling" },
  brand: { es: "Marca", en: "Brand" }, content: { es: "Contenido", en: "Content" }, seo: { es: "SEO", en: "SEO" },
  paid_media: { es: "Paid media", en: "Paid media" }, email: { es: "Email", en: "Email" },
  social_media: { es: "Redes sociales", en: "Social media" }, growth: { es: "Growth", en: "Growth" },
};

// Suggested specialty KEYS per profession (labels resolved from SPECIALTY_LABELS).
export const SPECIALTIES = {
  director: ["film", "documentaries", "commercials", "music_videos", "digital_content", "directing_actors", "production"],
  producer: ["film", "advertising", "events", "digital_content", "budgeting", "casting"],
  photographer: ["portrait", "product", "fashion", "events", "editorial", "commercial"],
  ux_ui_designer: ["ux_research", "ux_design", "ui_design", "prototyping", "design_systems", "usability_testing", "product_strategy"],
  developer: ["frontend", "backend", "fullstack", "mobile", "apis", "databases", "devops"],
  barber: ["cuts", "fades", "grooming", "beard", "events", "editorial_production"],
  hairstylist: ["color", "cuts", "styling", "bridal", "events", "editorial"],
  import_export_specialist: ["customs", "documentation", "sourcing", "freight", "distribution", "usa", "latin_america"],
  logistics_specialist: ["last_mile", "warehousing", "inventory", "freight", "distribution", "route_optimization"],
  manufacturing_specialist: ["factory_sourcing", "quality_control", "product_development", "packaging", "production_scaling"],
  marketing_specialist: ["brand", "content", "seo", "paid_media", "email", "social_media", "growth"],
};

// Resolve a stored specialty value to a localized label. Known keys → localized; custom free-text → as-is.
export function specialtyLabel(value, lang) {
  const def = SPECIALTY_LABELS[value];
  if (!def) return value;
  if (lang === "es") return def.es;
  if (lang === "am") return def.am ?? def.en ?? def.es;
  return def.en ?? def.es;
}

// Suggested specialties for a profession as {key,label} for rendering.
export function specialtySuggestions(profession, lang) {
  return (SPECIALTIES[profession] || []).map((key) => ({ key, label: specialtyLabel(key, lang) }));
}

// Visible profession card selector used in professional onboarding (exactly these options).
export const ONBOARDING_PROFESSIONS = [
  { value: "psychologist", icon: Brain, category: "clinical",
    es: "Psicólogo/a", en: "Psychologist",
    desc_es: "Profesional clínico con licencia", desc_en: "Licensed clinical professional" },
  { value: "coach", icon: Sparkles, category: "non_clinical",
    es: "Coach", en: "Coach",
    desc_es: "Acompañamiento y desarrollo personal", desc_en: "Personal development & guidance" },
  { value: "business_coach", icon: Briefcase, category: "non_clinical",
    es: "Mentor/a de negocios / Emprendedor/a", en: "Business Mentor / Entrepreneur",
    desc_es: "Negocios, emprendimiento y estrategia", desc_en: "Business, entrepreneurship & strategy" },
  { value: "career_coach", icon: Compass, category: "non_clinical",
    es: "Orientador/a profesional / de carrera", en: "Professional / Career Guide",
    desc_es: "Orientación profesional y de carrera", desc_en: "Professional & career guidance" },
  { value: "communication_coach", icon: MessageCircle, category: "non_clinical",
    es: "Especialista en comunicación", en: "Communication Specialist",
    desc_es: "Comunicación, oratoria y presencia", desc_en: "Communication, speaking & presence" },
  { value: "language_teacher", icon: Languages, category: "non_clinical",
    es: "Profesor/a de idiomas", en: "Language Teacher",
    desc_es: "Enseñanza y práctica de idiomas", desc_en: "Language teaching & practice" },
  { value: "other_non_clinical", icon: UserPlus, category: "non_clinical",
    es: "Otro profesional", en: "Other professional",
    desc_es: "Otro tipo de servicio profesional", desc_en: "Another kind of professional service" },
];

export const WORKS_WITH = [
  { value: "individuals", es: "Individuos", en: "Individuals" },
  { value: "couples", es: "Parejas", en: "Couples" },
  { value: "families", es: "Familias", en: "Families" },
  { value: "groups", es: "Grupos", en: "Groups" },
  { value: "organizations", es: "Organizaciones", en: "Organizations" },
  { value: "companies", es: "Empresas", en: "Companies" },
  { value: "ngos", es: "ONG / Fundaciones", en: "NGOs / Foundations" },
  { value: "schools", es: "Escuelas / Universidades", en: "Schools / Universities" },
  { value: "government", es: "Entidades gubernamentales", en: "Government entities" },
  { value: "insurance", es: "Aseguradoras", en: "Insurance organizations" },
];

export const ENGAGEMENT_TYPES = [
  { value: "individual_sessions", es: "Sesiones individuales", en: "Individual sessions" },
  { value: "group_sessions", es: "Sesiones grupales", en: "Group sessions" },
  { value: "workshops", es: "Talleres", en: "Workshops" },
  { value: "training", es: "Formación", en: "Training" },
  { value: "supervision", es: "Supervisión profesional", en: "Professional supervision" },
  { value: "staff_training", es: "Formación de personal", en: "Staff training" },
  { value: "crisis_response", es: "Respuesta a crisis / desastres", en: "Crisis / disaster response" },
  { value: "corporate_programs", es: "Programas corporativos", en: "Corporate programs" },
  { value: "speaking", es: "Charlas / paneles", en: "Speaking / panels" },
  { value: "consulting", es: "Consultoría", en: "Consulting" },
  { value: "project_based", es: "Proyectos", en: "Project-based" },
  { value: "initiative_participation", es: "Participación en iniciativas", en: "Initiative participation" },
];

export const GENDERS = [
  { value: "female", es: "Mujer", en: "Woman" },
  { value: "male", es: "Hombre", en: "Man" },
  { value: "non_binary", es: "No binario", en: "Non-binary" },
  { value: "other", es: "Otro", en: "Other" },
];

export const COMPENSATION_TYPES = [
  { value: "paid", es: "Remunerado", en: "Paid" },
  { value: "volunteer", es: "Voluntariado", en: "Volunteer" },
  { value: "to_be_discussed", es: "A convenir", en: "To be discussed" },
];

export const ORG_TYPES = [
  { value: "company", es: "Empresa", en: "Company" },
  { value: "ngo", es: "ONG / Fundación", en: "NGO / Foundation" },
  { value: "school", es: "Escuela / Universidad", en: "School / University" },
  { value: "government", es: "Entidad gubernamental", en: "Government entity" },
  { value: "insurance", es: "Aseguradora", en: "Insurance" },
  { value: "association", es: "Asociación profesional", en: "Professional association" },
  { value: "other", es: "Otra", en: "Other" },
];

export const OPPORTUNITY_TYPES = [
  { value: "group_sessions", es: "Sesiones grupales", en: "Group sessions" },
  { value: "training", es: "Formación", en: "Training" },
  { value: "workshops", es: "Talleres", en: "Workshops" },
  { value: "crisis_response", es: "Respuesta a crisis", en: "Crisis response" },
  { value: "supervision", es: "Supervisión profesional", en: "Professional supervision" },
  { value: "corporate", es: "Programa corporativo", en: "Corporate program" },
  { value: "consulting", es: "Consultoría", en: "Consulting" },
  { value: "project", es: "Proyecto", en: "Project-based" },
  { value: "other", es: "Otra", en: "Other" },
];

// Intent-led discovery. `apply` sets directory filters; clinical vs non-clinical stay distinct.
export const INTENTS = [
  { value: "therapy", es: "Terapia / apoyo clínico", en: "Therapy / clinical support", apply: { profession_category: "clinical" } },
  { value: "coaching", es: "Coaching", en: "Coaching", apply: { profession: "coach" } },
  { value: "communication", es: "Apoyo en comunicación", en: "Communication support", apply: { profession: "communication_coach" } },
  { value: "business", es: "Negocios / emprendimiento", en: "Business / entrepreneurship", apply: { profession: "business_coach" } },
  { value: "group", es: "Apoyo grupal", en: "Group support", apply: { engagement: "group_sessions" } },
  { value: "training", es: "Formación / taller", en: "Training / workshop", apply: { engagement: "workshops" } },
  { value: "org_project", es: "Proyecto organizacional", en: "Organizational project", apply: { engagement: "project_based" } },
  { value: "crisis", es: "Respuesta a crisis", en: "Crisis / disaster response", apply: { engagement: "crisis_response" } },
];

// Stable value keys stored in DB; labels localized for display and filtering.
export const POPULATIONS = [
  { value: "adults", es: "Adultos", en: "Adults" },
  { value: "adolescents", es: "Adolescentes", en: "Adolescents" },
  { value: "children", es: "Niños", en: "Children" },
  { value: "couples", es: "Parejas", en: "Couples" },
  { value: "families", es: "Familias", en: "Families" },
  { value: "older_adults", es: "Adultos mayores", en: "Older adults" },
  { value: "lgbtq", es: "Comunidad LGBTQ+", en: "LGBTQ+ community" },
  { value: "migrants", es: "Migrantes y expatriados", en: "Migrants & expats" },
  { value: "groups", es: "Grupos", en: "Groups" },
];

export const APPROACHES = [
  { value: "cbt", es: "Cognitivo-conductual (TCC)", en: "Cognitive Behavioral (CBT)" },
  { value: "psychodynamic", es: "Psicodinámico", en: "Psychodynamic" },
  { value: "humanistic", es: "Humanista", en: "Humanistic" },
  { value: "systemic", es: "Sistémico / familiar", en: "Systemic / family" },
  { value: "gestalt", es: "Gestalt", en: "Gestalt" },
  { value: "integrative", es: "Integrador", en: "Integrative" },
  { value: "act", es: "Aceptación y Compromiso (ACT)", en: "Acceptance & Commitment (ACT)" },
  { value: "emdr", es: "EMDR", en: "EMDR" },
  { value: "mindfulness", es: "Mindfulness", en: "Mindfulness" },
  { value: "other", es: "Otro", en: "Other" },
];

export const PAYMENT_METHODS = [
  { value: "cash", es: "Efectivo", en: "Cash" },
  { value: "card", es: "Tarjeta", en: "Card" },
  { value: "bank_transfer", es: "Transferencia bancaria", en: "Bank transfer" },
  { value: "paypal", es: "PayPal", en: "PayPal" },
  { value: "digital_wallet", es: "Billetera digital", en: "Digital wallet" },
  { value: "other", es: "Otro", en: "Other" },
];

export function localize(list, value, lang) {
  const item = list.find((x) => x.value === value);
  if (!item) return value;
  if (lang === "es") return item.es;
  if (lang === "am") return item.am ?? item.en ?? item.es;
  return item.en ?? item.es;
}

// Resolve a single label object ({es,en,am?}) with the am → en → es rule.
export function labelFor(obj, lang) {
  if (!obj) return "";
  if (lang === "es") return obj.es;
  if (lang === "am") return obj.am ?? obj.en ?? obj.es;
  return obj.en ?? obj.es;
}

const O = (value, es, en) => ({ value, es, en });

// Centralized date/time locale resolver (am → English fallback, es only for Spanish).
export function dateLocale(lang) {
  return lang === "es" ? "es-ES" : "en-US";
}

// Profession-specific onboarding config. Clinical professions reuse the legacy clinical
// fields (areas/populations_served/approach/insurance). Non-clinical professions store all
// structured data in `profession_details` (arrays of keys) + `qualifications` (free text),
// and NEVER render any clinical/health content.
export const PROFESSION_CONFIG = {
  psychologist: { category: "clinical", clinical: true },
  other_clinical: { category: "clinical", clinical: true },

  coach: {
    category: "non_clinical",
    qual: { es: "Certificaciones y formación en coaching", en: "Coaching certifications & training" },
    expertise: { es: "Tipo de coaching", en: "Type of coaching", options: [
      O("personal", "Coaching personal", "Personal coaching"), O("executive", "Coaching ejecutivo", "Executive coaching"),
      O("leadership", "Liderazgo", "Leadership"), O("career", "Carrera profesional", "Career"),
      O("productivity", "Productividad", "Productivity"), O("communication", "Comunicación", "Communication"),
      O("personal_dev", "Desarrollo personal", "Personal development"), O("transitions", "Transiciones", "Transitions"),
      O("accountability", "Accountability / seguimiento", "Accountability"), O("teams", "Equipos", "Teams"),
      O("other", "Otro", "Other") ] },
    who: { es: "¿Con quién trabajas?", en: "Who do you work with?", options: [
      O("professionals", "Profesionales", "Professionals"), O("entrepreneurs", "Emprendedores", "Entrepreneurs"),
      O("executives", "Ejecutivos", "Executives"), O("leaders", "Líderes / gerentes", "Leaders / managers"),
      O("teams", "Equipos", "Teams"), O("career_changers", "Personas en transición profesional", "People in career transition"),
      O("organizations", "Organizaciones", "Organizations"), O("other", "Otro", "Other") ] },
    services: { es: "¿Cómo trabajas?", en: "How do you work?", options: [
      O("individual", "Coaching individual", "Individual coaching"), O("group", "Coaching grupal", "Group coaching"),
      O("programs", "Programas", "Programs"), O("workshops", "Talleres", "Workshops"),
      O("teams", "Trabajo con equipos", "Team work"), O("org_programs", "Programas organizacionales", "Organizational programs") ] },
  },

  business_coach: {
    category: "non_clinical",
    qual: { es: "Experiencia y trayectoria empresarial", en: "Business background & experience" },
    expertise: { es: "¿En qué áreas de negocio tienes experiencia?", en: "Which business areas do you have experience in?", options: [
      O("entrepreneurship", "Emprendimiento", "Entrepreneurship"), O("strategy", "Estrategia de negocio", "Business strategy"),
      O("biz_dev", "Desarrollo de negocio", "Business development"), O("sales", "Ventas", "Sales"),
      O("marketing", "Marketing", "Marketing"), O("operations", "Operaciones", "Operations"),
      O("leadership", "Liderazgo", "Leadership"), O("growth", "Crecimiento", "Growth"),
      O("product", "Desarrollo de producto", "Product development"), O("customer_discovery", "Descubrimiento de clientes", "Customer discovery"),
      O("gtm", "Estrategia de salida al mercado", "Go-to-market"), O("pricing", "Estrategia de precios", "Pricing"),
      O("ecommerce", "Comercio electrónico", "E-commerce"), O("partnerships", "Alianzas", "Partnerships"),
      O("fundraising", "Captación de capital", "Fundraising"), O("planning", "Planificación de negocios", "Business planning"),
      O("team_building", "Formación de equipos", "Team building"), O("small_biz", "Operaciones de pequeña empresa", "Small business operations"),
      O("other", "Otro", "Other") ] },
    who: { es: "¿Con quién trabajas?", en: "Who do you work with?", options: [
      O("entrepreneurs", "Emprendedores", "Entrepreneurs"), O("founders", "Fundadores", "Founders"),
      O("startups", "Startups", "Startups"), O("small_biz", "Pequeñas empresas", "Small businesses"),
      O("growth_cos", "Empresas en crecimiento", "Growing companies"), O("independents", "Profesionales independientes", "Independent professionals"),
      O("leaders", "Líderes / gerentes", "Leaders / managers"), O("teams", "Equipos", "Teams"),
      O("organizations", "Organizaciones", "Organizations"), O("other", "Otro", "Other") ] },
    services: { es: "¿Cómo trabajas?", en: "How do you work?", options: [
      O("mentoring", "Mentoría individual", "1:1 mentoring"), O("advisory", "Asesoría estratégica", "Strategic advisory"),
      O("planning", "Sesiones de planificación", "Planning sessions"), O("workshops", "Talleres", "Workshops"),
      O("training", "Formación", "Training"), O("team", "Acompañamiento de equipos", "Team support"),
      O("projects", "Proyectos organizacionales", "Organizational projects"), O("programs", "Programas empresariales", "Business programs") ] },
    extras: [
      { key: "stages", es: "Etapas de negocio que dominas", en: "Business stages you understand", options: [
        O("idea", "Idea", "Idea"), O("early", "Etapa temprana", "Early stage"),
        O("growth", "Crecimiento", "Growth"), O("scale", "Escalado", "Scaling"), O("mature", "Consolidada", "Mature") ] },
      { key: "industries", es: "Industrias / sectores", en: "Industries / sectors", options: [
        O("tech", "Tecnología", "Technology"), O("retail", "Retail / comercio", "Retail"),
        O("services", "Servicios", "Services"), O("hospitality", "Hostelería / turismo", "Hospitality"),
        O("health", "Salud", "Health"), O("education", "Educación", "Education"),
        O("finance", "Finanzas", "Finance"), O("creative", "Creativo / medios", "Creative / media"),
        O("manufacturing", "Manufactura", "Manufacturing"), O("other", "Otro", "Other") ] },
    ],
  },

  career_coach: {
    category: "non_clinical",
    qual: { es: "Certificaciones y experiencia profesional", en: "Certifications & professional experience" },
    expertise: { es: "¿En qué áreas profesionales ayudas?", en: "Which professional areas do you help with?", options: [
      O("job_search", "Búsqueda de empleo", "Job search"), O("career_change", "Cambio de carrera", "Career change"),
      O("planning", "Planificación profesional", "Career planning"), O("cv", "CV / currículum", "CV / resume"),
      O("interviews", "Entrevistas", "Interviews"), O("positioning", "Posicionamiento profesional", "Professional positioning"),
      O("skills", "Desarrollo de habilidades", "Skills development"), O("leadership", "Liderazgo", "Leadership"),
      O("development", "Desarrollo profesional", "Professional development"), O("reentry", "Reincorporación laboral", "Return to work"),
      O("growth", "Crecimiento profesional", "Career growth"), O("other", "Otro", "Other") ] },
    who: { es: "¿Con quién trabajas?", en: "Who do you work with?", options: [
      O("students", "Estudiantes", "Students"), O("graduates", "Recién graduados", "Recent graduates"),
      O("early_career", "Profesionales al inicio de carrera", "Early-career professionals"),
      O("experienced", "Profesionales con experiencia", "Experienced professionals"), O("executives", "Ejecutivos", "Executives"),
      O("career_changers", "Personas cambiando de carrera", "Career changers"), O("job_seekers", "Personas buscando empleo", "Job seekers"),
      O("returners", "Personas regresando al mercado laboral", "People returning to work"), O("other", "Otro", "Other") ] },
    services: { es: "¿Cómo trabajas?", en: "How do you work?", options: [
      O("individual", "Individual", "Individual"), O("groups", "Grupos", "Groups"),
      O("workshops", "Talleres", "Workshops"), O("training", "Formación", "Training"),
      O("org_programs", "Programas organizacionales", "Organizational programs") ] },
  },

  communication_coach: {
    category: "non_clinical",
    qual: { es: "Formación y certificaciones", en: "Training & certifications" },
    expertise: { es: "Áreas de especialización", en: "Areas of expertise", options: [
      O("interpersonal", "Comunicación interpersonal", "Interpersonal communication"),
      O("professional", "Comunicación profesional", "Professional communication"),
      O("public_speaking", "Hablar en público", "Public speaking"), O("presentations", "Presentaciones", "Presentations"),
      O("leadership", "Comunicación de liderazgo", "Leadership communication"), O("teams", "Comunicación de equipos", "Team communication"),
      O("workplace", "Comunicación laboral", "Workplace communication"), O("intercultural", "Comunicación intercultural", "Intercultural communication"),
      O("conflict", "Comunicación en conflictos", "Conflict communication"), O("storytelling", "Storytelling", "Storytelling"),
      O("other", "Otro", "Other") ] },
    who: { es: "¿Con quién trabajas?", en: "Who do you work with?", options: [
      O("individuals", "Individuos", "Individuals"), O("professionals", "Profesionales", "Professionals"),
      O("leaders", "Líderes", "Leaders"), O("teams", "Equipos", "Teams"),
      O("companies", "Empresas", "Companies"), O("organizations", "Organizaciones", "Organizations"),
      O("students", "Estudiantes", "Students"), O("other", "Otro", "Other") ] },
    services: { es: "¿Cómo trabajas?", en: "How do you work?", options: [
      O("individual", "Individual", "Individual"), O("group", "Grupal", "Group"),
      O("workshops", "Talleres", "Workshops"), O("training", "Formación", "Training"),
      O("team_programs", "Programas de equipo", "Team programs"), O("org_projects", "Proyectos organizacionales", "Organizational projects") ] },
  },

  language_teacher: {
    category: "non_clinical",
    qual: { es: "Certificaciones docentes y experiencia", en: "Teaching certifications & experience" },
    languagesTaught: true,
    expertise: { es: "¿Qué tipo de enseñanza ofreces?", en: "What type of teaching do you offer?", options: [
      O("conversation", "Conversación", "Conversation"), O("structured", "Enseñanza estructurada", "Structured teaching"),
      O("pronunciation", "Pronunciación", "Pronunciation"), O("grammar", "Gramática", "Grammar"),
      O("business", "Idioma para negocios", "Business language"), O("travel", "Idioma para viajes", "Travel language"),
      O("exam_prep", "Preparación para exámenes", "Exam preparation"), O("writing", "Escritura", "Writing"),
      O("listening", "Comprensión auditiva", "Listening"), O("other", "Otro", "Other") ] },
    who: { es: "¿Con quién trabajas?", en: "Who do you work with?", options: [
      O("adults", "Adultos", "Adults"), O("adolescents", "Adolescentes", "Adolescents"),
      O("children", "Niños", "Children"), O("professionals", "Profesionales", "Professionals"),
      O("students", "Estudiantes", "Students"), O("companies", "Empresas", "Companies"),
      O("groups", "Grupos", "Groups"), O("other", "Otro", "Other") ] },
    services: { es: "¿Cómo trabajas?", en: "How do you work?", options: [
      O("individual", "Clases individuales", "Individual classes"), O("group", "Clases grupales", "Group classes"),
      O("conversation", "Práctica conversacional", "Conversation practice"), O("courses", "Cursos estructurados", "Structured courses"),
      O("corporate", "Formación empresarial", "Corporate training"), O("workshops", "Talleres", "Workshops") ] },
    extras: [
      { key: "levels", es: "¿Qué niveles enseñas?", en: "Which levels do you teach?", options: [
        O("beginner", "Principiante", "Beginner"), O("basic", "Básico", "Basic"),
        O("intermediate", "Intermedio", "Intermediate"), O("advanced", "Avanzado", "Advanced"),
        O("advanced_conv", "Conversación avanzada", "Advanced conversation"), O("all", "Todos los niveles", "All levels") ] },
    ],
  },

  other_non_clinical: {
    category: "non_clinical",
    qual: { es: "Cualificaciones y experiencia", en: "Qualifications & experience" },
    expertise: { es: "Áreas de especialización", en: "Areas of expertise", options: [
      O("consulting", "Consultoría", "Consulting"), O("training", "Formación", "Training"),
      O("advisory", "Asesoría", "Advisory"), O("creative", "Servicios creativos", "Creative services"),
      O("technical", "Servicios técnicos", "Technical services"), O("wellbeing", "Bienestar (no clínico)", "Wellbeing (non-clinical)"),
      O("education", "Educación", "Education"), O("other", "Otro", "Other") ] },
    who: { es: "¿Con quién trabajas?", en: "Who do you work with?", options: [
      O("individuals", "Individuos", "Individuals"), O("professionals", "Profesionales", "Professionals"),
      O("teams", "Equipos", "Teams"), O("companies", "Empresas", "Companies"),
      O("organizations", "Organizaciones", "Organizations"), O("students", "Estudiantes", "Students"),
      O("other", "Otro", "Other") ] },
    services: { es: "¿Cómo trabajas?", en: "How do you work?", options: [
      O("individual", "Individual", "Individual"), O("group", "Grupal", "Group"),
      O("workshops", "Talleres", "Workshops"), O("training", "Formación", "Training"),
      O("projects", "Proyectos", "Projects"), O("org_programs", "Programas organizacionales", "Organizational programs") ] },
  },
};

// Clean generic config for professions without profession-specific onboarding.
// No clinical fields and no expertise/who/services questions — they use the specialties layer.
export const GENERIC_CONFIG = {
  category: "non_clinical", generic: true,
  qual: { es: "Cualificaciones y experiencia", en: "Qualifications & experience" },
};

export function professionConfig(profession) {
  return PROFESSION_CONFIG[profession] || GENERIC_CONFIG;
}

export function formatDateRange(start, end, lang) {
  if (!start && !end) return "";
  // am has no dedicated date format → English locale (es only for Spanish UI).
  const loc = lang === "es" ? "es-ES" : "en-US";
  const opts = { year: "numeric", month: "short", day: "numeric" };
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(loc, opts);
  if (start && end && end !== start) return `${fmt(start)} – ${fmt(end)}`;
  return fmt(start || end);
}
